import Quill from 'quill/core';
import React, { useMemo, useState } from 'react';
import useAppSelector, {
  useAppDispatch,
} from '../../../app/redux/useAppSelector';
import { MenuItem } from '../../../components/Menu';
import PopOver from '../../../components/deprecated/PopOver';
import PopOverContainer from '../../../components/deprecated/PopOverContainer';
import annotationIcon from '../../../icons/non-prefilled/16x16/annotation.svg';
import reactionIcon from '../../../icons/non-prefilled/16x16/reaction.svg';
import EmojiPicker from '../../../post/comment/EmojiPicker';
import Sizes from '../../../utils/responsive/Sizes';
import { useIsSize } from '../../../utils/responsive/isSize';
import { useQuill } from '../../QuillContext';
import convertSelectionToCodeEmbed from '../../code/convertSelectionToCodeEmbed';
import { CREATION_ID, EditorActions } from '../../editorSlice';
import {
  boldIcon,
  codeIcon,
  italicIcon,
  linkIcon,
  strikethroughIcon,
} from '../editorIcons';
import Toolbar, { useToolbarOverflowMenuClose } from './Toolbar';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarHighlightButton from './buttons/ToolbarHighlightButton';
import type { ToolbarObjectButtonOptions } from './buttons/ToolbarObjectButton';
import ToolbarObjectButton from './buttons/ToolbarObjectButton';
import ToolbarCommentButton from './buttons/comments/ToolbarCommentButton';
import ToolbarReactionButton from './buttons/comments/ToolbarReactionButton';
import usePickReaction from './usePickReaction';

const ReactionMenuItem = () => {
  const closeOverflowMenu = useToolbarOverflowMenuClose();
  const handlePick = usePickReaction();
  const dispatch = useAppDispatch();
  const isSmall = useIsSize(Sizes.SM);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

  return (
    <PopOver
      popOver={
        <PopOverContainer noPadding>
          <EmojiPicker
            onPick={(picked: string) => {
              handlePick(picked);
              closeOverflowMenu();
            }}
          />
        </PopOverContainer>
      }
      trigger="click"
      placement={isSmall ? 'bottom' : 'right-start'}
      preventCloseOnContentClick
      open={isEmojiPickerOpen}
      onOpenChange={(open) => {
        setIsEmojiPickerOpen(open);
        dispatch(EditorActions.setIsPickingReaction(open));
      }}
    >
      <MenuItem icon={reactionIcon}>Add reaction</MenuItem>
    </PopOver>
  );
};

interface AnnotationMenuItemProps {
  onClick: () => void;
}

const AnnotationMenuItem = ({ onClick }: AnnotationMenuItemProps) => {
  const dispatch = useAppDispatch();

  return (
    <MenuItem
      icon={annotationIcon}
      onClick={() => {
        onClick();
        dispatch(
          EditorActions.focusAnnotation({
            id: CREATION_ID,
            isActive: true,
            hasExpandIntent: false,
          }),
        );
      }}
    >
      Add annotation
    </MenuItem>
  );
};

interface EditModeToolbarProps {
  multiline: boolean;
  inTitleLine: boolean;
  closeToolbar: () => void;
  onLinkSelected: () => void;
  objectButtonOptions?: ToolbarObjectButtonOptions;
}

const EditModeToolbar = ({
  multiline,
  inTitleLine,
  closeToolbar,
  onLinkSelected,
  objectButtonOptions,
}: EditModeToolbarProps) => {
  const {
    quill,
    selection: { formats },
  } = useQuill();

  const isSupportingCodeEmbed = useMemo(
    () => !!quill?.scroll.query('code-embed'),
    [quill],
  );
  const isSupportingInlineCode = useMemo(
    () => !!quill?.scroll.query('code'),
    [quill],
  );
  const isSupportingAnnotations = useMemo(
    () => !!quill?.scroll.query('annotation'),
    [quill],
  );
  const isSupportingComments = useMemo(
    () => !!quill?.scroll.query('comment'),
    [quill],
  );

  const lastCodeBlockSyntax = useAppSelector(
    (state) => state.ui.lastCodeBlockSyntax,
  );

  const handleFormat = (format: string, value: unknown) =>
    quill?.format(format, value, Quill.sources.USER);
  const toggleFormat = (name: string) => () =>
    handleFormat(name, !formats[name]);

  const existingComment = Array.isArray(formats.comment)
    ? (formats.comment as string[])
    : null;

  return (
    <Toolbar
      rightAlignOverflowMenu
      renderOverflowMenu={
        isSupportingAnnotations
          ? () => (
              <>
                {isSupportingComments && <ReactionMenuItem />}
                <AnnotationMenuItem onClick={closeToolbar} />
              </>
            )
          : undefined
      }
    >
      {!formats.table && (
        <ToolbarButtonGroup>
          <ToolbarObjectButton
            disabled={inTitleLine}
            options={objectButtonOptions}
          />
        </ToolbarButtonGroup>
      )}
      <ToolbarButtonGroup>
        <ToolbarButton
          accessibleType="toggle"
          icon={boldIcon}
          active={!!formats.bold}
          disabled={inTitleLine || !!formats.header}
          tooltip="Bold"
          shortcut="Ctrl + b"
          onClick={toggleFormat('bold')}
        />
        <ToolbarButton
          accessibleType="toggle"
          icon={italicIcon}
          active={!!formats.italic}
          disabled={inTitleLine}
          tooltip="Italic"
          shortcut="Ctrl + i"
          onClick={toggleFormat('italic')}
        />
        <ToolbarButton
          accessibleType="toggle"
          icon={strikethroughIcon}
          active={!!formats.strike}
          disabled={inTitleLine}
          tooltip="Strikethrough"
          shortcut="Ctrl + Alt + x"
          onClick={toggleFormat('strike')}
        />
        {isSupportingInlineCode && (
          <ToolbarButton
            icon={codeIcon}
            active={!!formats.code}
            disabled={inTitleLine || (multiline && !isSupportingCodeEmbed)}
            tooltip="Inline code"
            onClick={() => {
              if (!quill) return;

              if (!multiline) {
                handleFormat('code', !formats.code);
                return;
              }

              if (isSupportingCodeEmbed) {
                const range = quill.getSelection(true);
                convertSelectionToCodeEmbed(
                  quill,
                  range.index,
                  range.length,
                  lastCodeBlockSyntax,
                );
              }
            }}
          />
        )}
        <ToolbarButton
          icon={linkIcon}
          disabled={inTitleLine || multiline}
          active={!!formats.link}
          tooltip="Link"
          shortcut="Ctrl + k"
          onClick={onLinkSelected}
        />
      </ToolbarButtonGroup>
      {quill && (
        <ToolbarButtonGroup>
          <ToolbarHighlightButton
            quill={quill}
            value={formats.highlight}
            disabled={inTitleLine}
            onChange={(value) => handleFormat('highlight', value)}
          />
        </ToolbarButtonGroup>
      )}
      {isSupportingComments && (
        <ToolbarButtonGroup>
          {!isSupportingAnnotations && <ToolbarReactionButton />}
          <ToolbarCommentButton
            existingComment={existingComment}
            onClick={closeToolbar}
          />
        </ToolbarButtonGroup>
      )}
    </Toolbar>
  );
};

export default EditModeToolbar;
